import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/_services/language.service';
import { UploadService } from 'src/app/_services/upload.service';
import { UtilsService } from 'src/app/_shared/UtilsService';
import { Party, PartyType } from 'src/app/_models/party';
import { AdditionalPartyComponent } from 'src/app/_shared/component/additional-party/additional-party.component';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { RecaptchaEnterpriseService } from 'src/app/_services/recaptcha-enterprise.service';

@Component({
  standalone: false,
  selector: 'app-notice-of-unauthorized-use',
  templateUrl: './notice-of-unauthorized-use.component.html',
  styleUrls: ['./notice-of-unauthorized-use.component.scss']
})
export class NoticeOfUnauthorizedUseComponent implements OnInit, OnDestroy {

  @ViewChild('additionalPartyForm', {static: false}) private additionalPartyForm: AdditionalPartyComponent | undefined;
  siteKey = environment.recaptchaSiteKey;
  AMC_LANGUAGE_COOKIE = 'amc_lang';
  currentLanguage = 'en';
  submitted = false;
  blocked = false;
  privacyConsent = false;
  privacyConsentInvalid = false;

  // Preview dialog state
  previewOpen = false;
  previewText = '';
  previewHtml = '';
  generatedDate = '';

  additionalParties: Party[] = [];

  translateSubscription: Subscription | undefined;

  // Main filing form
  requestForm = this.fb.group({
    // Requesting party (Complainant)
    nameComplainant: ['', [Validators.required, Validators.maxLength(255)]],
    addressComplainant: ['', Validators.maxLength(255)],
    countryComplainantCode: ['', Validators.required],
    telephoneComplainant: ['', Validators.maxLength(255)],
    emailComplainant: ['', [Validators.required, this.utilsService.validateEmail, Validators.maxLength(255)]],
    representedByComplainant: ['', Validators.maxLength(255)],
    addressRepresentativeComplainant: ['', Validators.maxLength(255)],
    countryComplainantRepByCode: [''],
    telephoneRepresentativeComplainant: ['', Validators.maxLength(255)],
    emailRepresentativeComplainant: ['', [this.utilsService.validateEmail, Validators.maxLength(255)]],
    // Responding party (Respondent)
    nameRespondent: ['', [Validators.required, Validators.maxLength(255)]],
    addressRespondent: ['', Validators.maxLength(255)],
    countryRespondentCode: ['', Validators.required],
    telephoneRespondent: ['', Validators.maxLength(255)],
    emailRespondent: ['', [this.utilsService.validateEmail, Validators.maxLength(255)]],
    representedByRespondent: ['', Validators.maxLength(255)],
    addressRepresentativeRespondent: ['', Validators.maxLength(255)],
    countryRespondentRepByCode: [''],
    telephoneRepresentativeRespondent: ['', Validators.maxLength(255)],
    emailRepresentativeRespondent: ['', [this.utilsService.validateEmail, Validators.maxLength(255)]],
    // Notice body
    disputeDescription: ['', [Validators.required, Validators.maxLength(18000)]],
    requestedAction: ['', [Validators.required, Validators.maxLength(18000)]]
  });

  countryOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private uploadService: UploadService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    public languageService: LanguageService,
    private recaptchaService: RecaptchaEnterpriseService
  ) { }

  ngOnInit(): void {
    this.recaptchaService.load();
    this.translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.currentLanguage = this.translate.currentLang;
    });
    this.countryOptions = this.utilsService.loadCountryOption();
  }

  ngOnDestroy() {
    this.recaptchaService.unload();
    if (this.translateSubscription) { this.translateSubscription.unsubscribe() }
  }

  get f() { return this.requestForm.controls; }

  /** Resolve a country CODE (e.g. "CH") to its English label. */
  private countryName(code: string): string {
    if (!code) { return ''; }
    const match = this.countryOptions.find(c => c.value === code);
    return match ? match.label : code;
  }

  // ---------- Notice generation ----------

  async generateNotice(): Promise<void> {
    this.submitted = true;
    this.privacyConsentInvalid = !this.privacyConsent;
    if (this.requestForm.invalid || this.privacyConsentInvalid) { return; }
    if (!this.additionalPartyForm?.validate()) { return; }

    this.blocked = true;

    const countryComplainantName = await this.utilsService.getCountryByCode(this.requestForm.value['countryComplainantCode']);
    const countryRespondentName = await this.utilsService.getCountryByCode(this.requestForm.value['countryRespondentCode']);
    const countryRepComplainantName = await this.utilsService.getCountryByCode(this.requestForm.value['countryComplainantRepByCode']);
    const countryRepRespondentName = await this.utilsService.getCountryByCode(this.requestForm.value['countryRespondentRepByCode']);

    const formData = new FormData();
    Object.keys(this.requestForm.controls).forEach((key) => {
      if (this.requestForm.value[key] === null) {
        formData.append(key, '');
      } else {
        formData.append(key, this.requestForm.value[key]);
      }
    });
    if (countryComplainantName) { formData.append('countryComplainant', countryComplainantName); }
    if (countryRespondentName) { formData.append('countryRespondent', countryRespondentName); }
    if (countryRepComplainantName) { formData.append('countryComplainantRepBy', countryRepComplainantName); }
    if (countryRepRespondentName) { formData.append('countryRespondentRepBy', countryRepRespondentName); }
    formData.append('additionalParties', JSON.stringify(this.additionalParties));
    formData.append('language', this.languageService.currentLanguage || this.translate.currentLang || this.currentLanguage);

    try {
      const recaptchaToken = await this.recaptchaService.executeScore('submit_notice');
      formData.append('g_recaptcha_response', recaptchaToken);
      this.uploadService.submitNoticeForm(formData).subscribe(
        response => {
          if (response instanceof HttpResponse) {
            if (response.status === 201) {
              this.blocked = false;
              const v = (key: string) => (this.requestForm.value[key] || '').toString().trim();
              const dateLocale = this.languageService.currentLanguage || this.translate.currentLang || this.currentLanguage;
              this.generatedDate = new Date().toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' });
              this.previewText = this.buildPlainText(v);
              this.previewHtml = this.buildHtml(v);
              this.previewOpen = true;
            } else {
              this.blocked = false;
            }
          }
        }, err => {
          this.blocked = false;
        }, () => { this.blocked = false; });
    } catch (e) {
      this.blocked = false;
    }
  }

  closePreview(): void {
    this.previewOpen = false;
  }

  onPrivacyConsentChange(): void {
    if (this.submitted) {
      this.privacyConsentInvalid = !this.privacyConsent;
    }
  }

  downloadDocx(): void {
    if (!this.previewText) {
      return;
    }
    const v = (key: string) => (this.requestForm.value[key] || '').toString().trim();


    const rightParagraph = (text: string) => new Paragraph({
      alignment: AlignmentType.RIGHT,
      children: [new TextRun({ text, font: 'Arial' })]
    });

    const normalParagraph = (text: string, bold = false, italic = false) => new Paragraph({
      children: [new TextRun({ text, bold, italics: italic, font: 'Arial' })]
    });

    const emptyLine = () => new Paragraph({ children: [] });

    const paragraphs: Paragraph[] = [];

    // Title
    paragraphs.push(new Paragraph({
      children: [new TextRun({ text: this.t('noticeOfUnauthorizedUse.pageTitle'), bold: true, size: 28, font: 'Arial' })]
    }));
    paragraphs.push(emptyLine());

    // Respondent info (right-aligned)
    const resFields = [v('nameRespondent'), v('addressRespondent'), this.countryName(v('countryRespondentCode')), v('telephoneRespondent'), v('emailRespondent')];
    resFields.filter(f => f).forEach(f => paragraphs.push(normalParagraph(f)));

    // Respondent representative block
    const repByRespondentDocx = v('representedByRespondent');
    if (repByRespondentDocx) {
      paragraphs.push(emptyLine());
      paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.representedBy') + ':'));
      const repResFields = [repByRespondentDocx, v('addressRepresentativeRespondent'), this.countryName(v('countryRespondentRepByCode')), v('telephoneRepresentativeRespondent'), v('emailRepresentativeRespondent')];
      repResFields.filter(f => f).forEach(f => paragraphs.push(normalParagraph(f)));
    }
    paragraphs.push(emptyLine());

    // Date
    paragraphs.push(normalParagraph(this.generatedDate));
    paragraphs.push(emptyLine());

    // Notice body
    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeRe'), true));
    paragraphs.push(emptyLine());
    paragraphs.push(normalParagraph(this.translate.instant('noticeOfUnauthorizedUse.noticeGreeting', { respondentName: v('nameRespondent') })));
    paragraphs.push(emptyLine());
    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeAttentionIntro')));
    paragraphs.push(emptyLine());

    // disputeDescription (may be multiline)
    v('disputeDescription').split('\n').forEach((line: string) => paragraphs.push(normalParagraph(line)));
    paragraphs.push(emptyLine());

    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeRequestIntro')));
    paragraphs.push(emptyLine());

    // Requested action (may be multiline)
    v('requestedAction').split('\n').forEach((line: string) => paragraphs.push(normalParagraph(line)));
    paragraphs.push(emptyLine());

    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeComplianceRequest')));
    paragraphs.push(emptyLine());

    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeClosingParagraph')));
    paragraphs.push(emptyLine());

    // Sincerely + Complainant info (right-aligned)
    paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.noticeSincerely')));
    paragraphs.push(emptyLine());
    const reqFields = [v('nameComplainant'), v('addressComplainant'), this.countryName(v('countryComplainantCode')), v('telephoneComplainant'), v('emailComplainant')];
    reqFields.filter(f => f).forEach(f => paragraphs.push(normalParagraph(f)));

    // Representative block
    const repBy = v('representedByComplainant');
    if (repBy) {
      paragraphs.push(emptyLine());
      paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.representedBy') + ':'));
      const repFields = [repBy, v('addressRepresentativeComplainant'), this.countryName(v('countryComplainantRepByCode')), v('telephoneRepresentativeComplainant'), v('emailRepresentativeComplainant')];
      repFields.filter(f => f).forEach(f => paragraphs.push(normalParagraph(f)));
    }

    // Additional parties
    if (this.additionalParties.length) {
      paragraphs.push(emptyLine());
      paragraphs.push(normalParagraph(this.t('noticeOfUnauthorizedUse.additionalParties') + ':', true));
      this.additionalParties.forEach(p => {
        const label = p.type === PartyType.complainant
          ? this.t('noticeOfUnauthorizedUse.additionalRequestingParty')
          : this.t('noticeOfUnauthorizedUse.additionalRespondingParty');
        paragraphs.push(normalParagraph(`${label}: ${p.name}`));
        if (p.address) { paragraphs.push(normalParagraph(`  ${this.t('address')}: ${p.address}`)); }
        if (p.country) { paragraphs.push(normalParagraph(`  ${this.t('country')}: ${p.country}`)); }
        if (p.telephone) { paragraphs.push(normalParagraph(`  ${this.t('tel')}: ${p.telephone}`)); }
        if (p.email) { paragraphs.push(normalParagraph(`  ${this.t('email')}: ${p.email}`)); }
      });
    }

    const doc = new Document({
      sections: [{ children: paragraphs }],
      styles: {
        default: {
          document: {
            run: {
              font: 'Arial',
            },
          },
        },
      },
    });

    Packer.toBlob(doc).then(blob => {
      saveAs(blob, `notice-unauthorized-use-${new Date().toISOString().slice(0, 10)}.docx`);
    });
  }

  copyToClipboard(): void {
    if (!this.previewText) {
      return;
    }

    const copyFallback = (): boolean => {
      const textarea = document.createElement('textarea');
      textarea.value = this.previewText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (e) {
        success = false;
      }
      document.body.removeChild(textarea);
      return success;
    };

    const showSuccess = () => {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: this.translate.instant('noticeOfUnauthorizedUse.copiedSuccess'),
        showConfirmButton: false,
        timer: 2000
      });
    };

    const showError = () => {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: this.translate.instant('noticeOfUnauthorizedUse.copyFailed'),
        showConfirmButton: false,
        timer: 2000
      });
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.previewText).then(showSuccess).catch(() => {
        copyFallback() ? showSuccess() : showError();
      });
    } else {
      copyFallback() ? showSuccess() : showError();
    }
  }

  // ---------- Builders ----------

  private t(key: string): string { return this.translate.instant(key); }

  private buildPlainText(v: (k: string) => string): string {
    const addBlock = this.additionalParties.length ? this.buildAdditionalPartiesText() : '';
    const lines: string[] = [];

    const pushValue = (value: string) => {
      const text = (value || '').trim();
      if (text) {
        lines.push(text);
      }
    };

    const pushSpacer = () => {
      if (lines.length && lines[lines.length - 1] !== '') {
        lines.push('');
      }
    };

    // Title
    pushValue(this.t('noticeOfUnauthorizedUse.pageTitle'));
    pushSpacer();

    // Respondent block
    [
      v('nameRespondent'),
      v('addressRespondent'),
      this.countryName(v('countryRespondentCode')),
      v('telephoneRespondent'),
      v('emailRespondent')
    ].forEach(pushValue);

    // Respondent representative block
    const repByRespondentText = v('representedByRespondent');
    if (repByRespondentText) {
      pushSpacer();
      pushValue(this.t('noticeOfUnauthorizedUse.representedBy') + ':');
      [
        repByRespondentText,
        v('addressRepresentativeRespondent'),
        this.countryName(v('countryRespondentRepByCode')),
        v('telephoneRepresentativeRespondent'),
        v('emailRepresentativeRespondent')
      ].forEach(pushValue);
    }
    pushSpacer();

    // Date + body
    pushValue(this.generatedDate);
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeRe'));
    pushSpacer();
    pushValue(this.translate.instant('noticeOfUnauthorizedUse.noticeGreeting', { respondentName: v('nameRespondent') }));
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeAttentionIntro'));
    pushSpacer();
    pushValue(v('disputeDescription'));
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeRequestIntro'));
    pushSpacer();
    pushValue(v('requestedAction'));
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeComplianceRequest'));
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeClosingParagraph'));
    pushSpacer();
    pushValue(this.t('noticeOfUnauthorizedUse.noticeSincerely'));
    pushSpacer();

    // Complainant block
    [
      v('nameComplainant'),
      v('addressComplainant'),
      this.countryName(v('countryComplainantCode')),
      v('telephoneComplainant'),
      v('emailComplainant')
    ].forEach(pushValue);

    // Representative block (only if representative exists)
    const repBy = v('representedByComplainant');
    if (repBy) {
      pushSpacer();
      pushValue(this.t('noticeOfUnauthorizedUse.representedBy') + ':');
      [
        repBy,
        v('addressRepresentativeComplainant'),
        this.countryName(v('countryComplainantRepByCode')),
        v('telephoneRepresentativeComplainant'),
        v('emailRepresentativeComplainant')
      ].forEach(pushValue);
    }

    if (addBlock) {
      pushSpacer();
      pushValue(addBlock);
    }

    pushSpacer();

    return lines.join('\n') + '\n';
  }

  private buildAdditionalPartiesText(): string {
    const lines: string[] = ['', this.t('noticeOfUnauthorizedUse.additionalParties') + ':'];
    this.additionalParties.forEach(p => {
      const label = p.type === PartyType.complainant
        ? this.t('noticeOfUnauthorizedUse.additionalRequestingParty')
        : this.t('noticeOfUnauthorizedUse.additionalRespondingParty');
      lines.push(`- ${label}: ${p.name}`);
      if (p.address) { lines.push(`  ${this.t('address')}: ${p.address}`); }
      if (p.country) { lines.push(`  ${this.t('country')}: ${p.country}`); }
      if (p.telephone) { lines.push(`  ${this.t('tel')}: ${p.telephone}`); }
      if (p.email) { lines.push(`  ${this.t('email')}: ${p.email}`); }
    });
    return lines.join('\n');
  }

  private esc(s: string): string {
    return (s || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]!));
  }

  private buildHtml(v: (k: string) => string): string {
    const addPartiesHtml = this.additionalParties.length
      ? `<div class="block"><strong>${this.esc(this.t('noticeOfUnauthorizedUse.additionalParties'))}:</strong><br/>
          ${this.additionalParties.map(p => `
            <div class="block">
              <em>${p.type === PartyType.complainant
                ? this.esc(this.t('noticeOfUnauthorizedUse.additionalRequestingParty'))
                : this.esc(this.t('noticeOfUnauthorizedUse.additionalRespondingParty'))}</em><br/>
              ${this.esc(p.name || '')}<br/>
              ${p.address ? this.esc(p.address) + '<br/>' : ''}
              ${p.country ? this.esc(p.country) + '<br/>' : ''}
              ${p.telephone ? this.esc(p.telephone) + '<br/>' : ''}
              ${p.email ? this.esc(p.email) + '<br/>' : ''}
            </div>`).join('')}
        </div>`
      : '';

    const repRespondentHtml = v('representedByRespondent') ? `
  <div class="right">
    <strong>${this.esc(this.t('noticeOfUnauthorizedUse.representedBy'))}:</strong><br/>
    ${this.esc(v('representedByRespondent'))}<br/>
    ${v('addressRepresentativeRespondent') ? this.esc(v('addressRepresentativeRespondent')) + '<br/>' : ''}
    ${v('countryRespondentRepByCode') ? this.esc(this.countryName(v('countryRespondentRepByCode'))) + '<br/>' : ''}
    ${v('telephoneRepresentativeRespondent') ? this.esc(v('telephoneRepresentativeRespondent')) + '<br/>' : ''}
    ${v('emailRepresentativeRespondent') ? this.esc(v('emailRepresentativeRespondent')) : ''}
  </div>` : '';

    const repHtml = v('representedByComplainant') ? `
  <div class="right">
    <strong>${this.esc(this.t('noticeOfUnauthorizedUse.representedBy'))}:</strong><br/>
    ${this.esc(v('representedByComplainant'))}<br/>
    ${v('addressRepresentativeComplainant') ? this.esc(v('addressRepresentativeComplainant')) + '<br/>' : ''}
    ${v('countryComplainantRepByCode') ? this.esc(this.countryName(v('countryComplainantRepByCode'))) + '<br/>' : ''}
    ${v('telephoneRepresentativeComplainant') ? this.esc(v('telephoneRepresentativeComplainant')) + '<br/>' : ''}
    ${v('emailRepresentativeComplainant') ? this.esc(v('emailRepresentativeComplainant')) : ''}
  </div>` : '';

    return `<!DOCTYPE html>
    <html lang="en">
    <head>
    <meta charset="UTF-8" />
    <title>${this.esc(this.t('noticeOfUnauthorizedUse.pageName'))}</title>
    <style>
    body{font-family:Arial,Helvetica,sans-serif;color:#000;line-height:1.45;margin:32px;}
    h1{font-size:18px;margin:0 0 18px;font-weight:bold;}
    .conf{color:#d80000;font-weight:bold;margin-top:40px;}
    .block{margin:10px 0;}
    .right{text-align:right;margin:10px 0;}
    </style>
    </head>
    <body>
  <h1>${this.esc(this.t('noticeOfUnauthorizedUse.pageTitle'))}</h1>
  <div class="right">
    ${this.esc(v('nameRespondent'))}<br/>
    ${this.esc(v('addressRespondent'))}<br/>
    ${this.esc(this.countryName(v('countryRespondentCode')))}<br/>
    ${this.esc(v('telephoneRespondent'))}<br/>
    ${this.esc(v('emailRespondent'))}
  </div>
  ${repRespondentHtml}
  <div class="right">${this.esc(this.generatedDate)}</div>
  <div class="block">${this.esc(this.t('noticeOfUnauthorizedUse.noticeRe'))}</div>
  <div class="block">${this.esc(this.translate.instant('noticeOfUnauthorizedUse.noticeGreeting', { respondentName: v('nameRespondent') }))}</div>
  <div class="block">${this.esc(this.t('noticeOfUnauthorizedUse.noticeAttentionIntro'))}</div>
  <div class="block"><em>${this.esc(v('disputeDescription')).replace(/\n/g, '<br/>')}</em></div>
  <div class="block">${this.esc(this.t('noticeOfUnauthorizedUse.noticeRequestIntro'))}</div>
  <div class="block"><em>${this.esc(v('requestedAction')).replace(/\n/g, '<br/>')}</em></div>
  <div class="block">${this.esc(this.t('noticeOfUnauthorizedUse.noticeComplianceRequest'))}</div>
  <div class="block">${this.esc(this.t('noticeOfUnauthorizedUse.noticeClosingParagraph'))}</div>
  <div class="right">${this.esc(this.t('noticeOfUnauthorizedUse.noticeSincerely'))}</div>
  <div class="right">
    ${this.esc(v('nameComplainant'))}<br/>
    ${this.esc(v('addressComplainant'))}<br/>
    ${this.esc(this.countryName(v('countryComplainantCode')))}<br/>
    ${this.esc(v('telephoneComplainant'))}<br/>
    ${this.esc(v('emailComplainant'))}
  </div>
  ${repHtml}
  ${addPartiesHtml}
</body>
</html>`;
  }
}
